import { copy } from "../../utils/copy";
import {
  Square2StackIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";

type CopyButtonProps = {
  title: string;
  text?: string;
  showToast?: any;
};

export function CopyBtn({ title, text, showToast }: CopyButtonProps) {
  const copyToClipboard = async (title: string, text: string) => {
    const success = await copy(text);
    if (success) {
      showToast(title, "", <Square2StackIcon height={20} />);
    }
  };

  return (
    <button
      className="flex items-center justify-center px-2 py-1 border-2 border-lavender-500 text-lavender-500 rounded-md hover:bg-lavender-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-lavender-800 focus:ring-opacity-50 transition-colors duration-200"
      onClick={() => text && copyToClipboard(title, text)}
      disabled={!text}
      aria-label={title}
    >
      <Square2StackIcon height={20} />
      <span className="ml-4">Copy</span>
    </button>
  );
}

export function InfoRow({
  label,
  value,
  showToast,
  copyTitle,
  copy = true,
}: {
  label: string;
  value?: string;
  showToast?: any;
  copyTitle?: string;
  copy?: boolean;
}) {
  return (
    <div className="flex w-full items-center justify-between py-4">
      <div className="flex-1">
        <div className="mb-1 text-md font-semibold">{label}</div>
        <div className="text-sm text-gray-600 break-all">{value || "-"}</div>
      </div>
      {copy && value && showToast && (
        <CopyBtn
          title={copyTitle || label}
          text={value}
          showToast={showToast}
        />
      )}
    </div>
  );
}

export function AddressBlock({ title, addr }: { title: string; addr?: any }) {
  if (!addr) return null;
  return (
    <div className="flex justify-between items-start w-full py-4">
      <div className="text-md font-semibold mb-2">{title}</div>
      <address className="not-italic text-right text-gray-600">
        {addr.name && <p>{addr.name}</p>}
        {addr.street1 && <p>{addr.street1}</p>}
        {addr.street2 && <p>{addr.street2}</p>}
        {addr.city && addr.state && (
          <p>{`${addr.city}, ${addr.state} ${addr.postalCode ?? ""}`}</p>
        )}
      </address>
    </div>
  );
}

export function MaskedValue({
  label,
  value,
  visible,
  toggleVisible,
  showToast,
  copy = true,
}: {
  label: string;
  value?: string;
  visible: boolean;
  toggleVisible: () => void;
  showToast?: any;
  copy?: boolean;
}) {
  const masked = !value
    ? ""
    : "•".repeat(Math.max(0, value.length - 4)) + value.slice(-4);

  return (
    <div className="flex w-full items-center justify-between py-4">
      <div className="flex-1">
        <div className="mb-1 text-md font-semibold">
          {label}{" "}
          <button
            onClick={toggleVisible}
            className="hover:bg-gray-200 rounded"
            aria-label="Toggle visibility"
          >
            {visible ? (
              <EyeIcon className="h-4 w-4" />
            ) : (
              <EyeSlashIcon className="h-4 w-4" />
            )}
          </button>
        </div>
        <div className="text-sm text-gray-600">{visible ? value : masked}</div>
      </div>
      {copy && value && showToast && (
        <CopyBtn title={label} text={value} showToast={showToast} />
      )}
    </div>
  );
}
